import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { isEmail } from 'react-multi-email';
import Button from 'components/button';
import Modal from 'components/modal';
import { MODAL_SIZES } from 'components/modal/types';
import Notification from 'components/notification';
import ToggleButtons from 'components/toggleButtons/toggleButtons';
import { CONTENT_TYPES } from 'constants/playlists';
import { getDomain } from 'constants/utilities/emails';
import { NOTIFICATION_TYPES } from 'constants/components/notification';
import {
  LICENSE_REJECTION_OUTCOMES,
  licenseRejectionMessage,
} from 'constants/errors/license_error_codes';
import {
  ACCOUNT_USERS_NOT_LOADED,
  EMAILS_NOT_CHECKED,
  INVITATIONS_NOT_SENT,
  TEAM_INVITE_WIZARD_COPY,
  activeUsersInvitedToTeam,
  inviteUsersToTeamTitle,
  pendingUsersInvitedToTeam,
} from 'constants/invites';
import { SessionContext } from 'contexts/session';
import { isTeamScopedManager } from 'contexts/session/utilities';
import { Team, UserTeam } from 'contexts/teams/types';
import { UserInvites } from 'contexts/users/types';
import {
  INVITE_NARROW_STEP_WIDTH,
  INVITE_TYPE_OPTIONS,
  InviteType,
  SKILL_DIVE_ACCESS_STEP_WIDTH,
} from 'pages/Users/components/InviteUsers';
import { Invite } from 'pages/Users/components/InviteUsers/AddByEmails/invitesReducer';
import AddByLink from 'pages/Users/components/InviteUsers/AddByLink/AddByLink';
import AddMessage from 'pages/Users/components/InviteUsers/AddMessage';
import AdjustUsersSettings from 'pages/Users/components/InviteUsers/AdjustUsersSettings';
import Footer from 'pages/Users/components/InviteUsers/Footer';
import RemoveUsers from 'pages/Users/components/InviteUsers/RemoveUsers/RemoveUsers';
import {
  BodyContainer,
  HeaderContainer,
  InnerContainer,
  Title,
} from 'pages/Users/components/InviteUsers/styles';
import { getInvitations } from 'services/users';
import {
  alreadyInvitedReason,
  applySkillDiveDependency,
  checkAlreadyInvited,
  licenseSeats,
  takenLicenses,
  withLiveAccount,
} from 'utils/invites';
import {
  applyTeamInvites,
  outcomeFailed,
  outcomeHitLicenseError,
  outcomeSavedAnything,
} from './applyTeamInvites';
import {
  INVALID_DOMAIN_ERROR,
  defaultInviteMessage,
  toExistingInvite,
  userFullName,
} from './helpers';
import InviteOutcome from './InviteOutcome';
import SelectUsersStep from './SelectUsersStep';
import { EmailError, ITeamInviteWizard, TeamInviteOutcome, UsersStatus, WizardStep } from './types';
import { AccessStepFieldset, OutcomeActions, StepFieldset } from './styles';
import { colors } from 'constants/styles/colors';

// Steps: pick users and emails, access, message. Without new emails, access is the last step.
const STEP_DOTS = 3;
const stepDot = (step: WizardStep) =>
  (step === WizardStep.ACCESS && 1) || (step === WizardStep.MESSAGE && 2) || 0;

// Team invite modal: adds existing users to the team right away and invites new emails.
const TeamInviteWizard: React.FC<ITeamInviteWizard> = ({
  team,
  account: openedWithAccount,
  isInvite,
  onClose,
}) => {
  const {
    user: sessionUser,
    account: sessionAccount,
    actions: { refreshAccounts, refreshSession },
    isAccountOwner,
    isTeamManager,
    isStaff,
  } = useContext(SessionContext);
  const account = withLiveAccount(openedWithAccount, sessionAccount);
  // The existing-users list only covers a Team Manager's own teams.
  const managedTeamsOnly = isTeamScopedManager({ isTeamManager, isAccountOwner, isStaff });

  const [inviteType, setInviteType] = useState<InviteType>(InviteType.BY_EMAIL);
  const [step, setStep] = useState<WizardStep>(WizardStep.SELECT);
  const [users, setUsers] = useState<UserInvites[]>([]);
  const [usersStatus, setUsersStatus] = useState<UsersStatus>('loading');
  const [existingUsers, setExistingUsers] = useState<Invite[]>([]);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInvites, setEmailInvites] = useState<Invite[]>([]);
  const [errors, setErrors] = useState<EmailError[]>([]);
  const [checkingEmails, setCheckingEmails] = useState(false);
  const [editingOnMobile, setEditingOnMobile] = useState(false);
  const [message, setMessage] = useState(() =>
    defaultInviteMessage(sessionUser ? userFullName(sessionUser) : '', team.name)
  );
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<TeamInviteOutcome | null>(null);
  const mounted = useRef(true);

  // Every row joins this team, as a manager or not.
  const teamEntry: UserTeam = useMemo(
    () => ({ team, team_id: team.id, name: team.name, is_manager: false }),
    [team]
  );

  const isAccountDomain = (email: string) =>
    Boolean(account?.email_domains?.some(({ email_domain }) => email_domain === getDomain(email)));
  const invalidEmails = emails.filter((email) => !isEmail(email));
  // Training seats left: `true` is unlimited, and below zero when more are assigned than owned.
  const trainingSeats =
    typeof account.licenses_available === 'number'
      ? account.licenses_available
      : account.licenses_available
      ? Number.POSITIVE_INFINITY
      : 0;
  // Backstop: blocks saving when the rows take more training seats than are left.
  const seatRows = [...emailInvites, ...existingUsers];
  const trainingOverCommitted =
    licenseSeats(seatRows).gained > 0 && takenLicenses(seatRows) > trainingSeats;
  // Only new emails get the message.
  const needsMessage = emailInvites.length > 0;

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  // The account's active users who aren't on the team yet.
  useEffect(() => {
    getInvitations({
      active: 'true',
      license: null,
      not_in_teams: team.id,
      roles: null,
      page: 1,
      page_size: Number.MAX_SAFE_INTEGER,
      search: null,
    })
      .then((response) => {
        if (!mounted.current) return;
        setUsers(response.data.results ?? []);
        setUsersStatus('ready');
      })
      .catch(() => {
        if (!mounted.current) return;
        setUsersStatus('failed');
        Notification({ text: ACCOUNT_USERS_NOT_LOADED, type: NOTIFICATION_TYPES.ERROR });
      });
  }, [team.id]);

  // One row per valid email; new rows get training while seats last, and no Skill Dive.
  useEffect(() => {
    setEmailInvites((previous) => {
      const seats = trainingSeats - takenLicenses(existingUsers);
      const rows: Invite[] = [];
      emails
        .filter((email) => isEmail(email) && isAccountDomain(email))
        .forEach((email) => {
          if (rows.some((row) => row.email === email)) return;
          const kept = previous.find((invite) => invite.email === email);
          const licensed = rows.filter(({ has_license }) => has_license).length;
          rows.push(
            kept ?? {
              email,
              is_manager: false,
              has_license: licensed < seats,
              has_skill_dive: false,
              userTeams: [{ ...teamEntry }],
            }
          );
        });
      return applySkillDiveDependency(rows);
    });
  }, [emails]);

  const handleToggleUser = (user: UserInvites) => {
    setExistingUsers((previous) =>
      previous.some(({ id }) => id === user.id)
        ? previous.filter(({ id }) => id !== user.id)
        : [...previous, toExistingInvite(user, teamEntry)]
    );
  };

  const handleRemoveUser = (userId: string) => {
    setExistingUsers((previous) => previous.filter(({ id }) => id !== userId));
  };

  // Lists new emails the server rejects, that are already in use or outside the account's domains.
  const handleSelectNext = async () => {
    const wellFormed = emails.filter((email) => isEmail(email));
    if (!wellFormed.length) {
      setStep(WizardStep.ACCESS);
      return;
    }
    setCheckingEmails(true);
    try {
      const { rejected, alreadyInvited } = await checkAlreadyInvited(wellFormed);
      const inUse = alreadyInvited.map((invitation) => ({
        email: invitation.email,
        error: alreadyInvitedReason(invitation, account.id),
      }));
      const outsideDomains = wellFormed
        .filter((email) => !isAccountDomain(email))
        .map((email) => ({ email, error: INVALID_DOMAIN_ERROR }));
      const found = [...rejected, ...inUse, ...outsideDomains].filter(
        (error, index, all) => all.findIndex(({ email }) => email === error.email) === index
      );
      setErrors(found);
      setStep(found.length ? WizardStep.ERRORS : WizardStep.ACCESS);
    } catch (error) {
      Notification({ text: EMAILS_NOT_CHECKED, type: NOTIFICATION_TYPES.ERROR });
    } finally {
      setCheckingEmails(false);
    }
  };

  const handleRemoveErrors = () => {
    const rejected = errors.map(({ email }) => email);
    const remaining = emails.filter((email) => !rejected.includes(email));
    setEmails(remaining);
    setErrors([]);
    const anyLeft = existingUsers.length > 0 || remaining.some((email) => isEmail(email));
    setStep(anyLeft ? WizardStep.ACCESS : WizardStep.SELECT);
  };

  const handleAccessUpdate = (updated: Invite[]) => {
    setExistingUsers(applySkillDiveDependency(updated.filter(({ id }) => id)));
    setEmailInvites(applySkillDiveDependency(updated.filter(({ id }) => !id)));
  };

  const handleRemoveRow = (email: string) => {
    const nextUsers = existingUsers.filter((invite) => invite.email !== email);
    const nextEmails = emails.filter((entry) => entry !== email);
    setExistingUsers(nextUsers);
    setEmails(nextEmails);
    if (
      !nextUsers.length &&
      !nextEmails.some((entry) => isEmail(entry) && isAccountDomain(entry))
    ) {
      setStep(WizardStep.SELECT);
    }
  };

  const handleSend = async () => {
    setSending(true);
    const result = await applyTeamInvites({
      teamId: team.id,
      existingUsers,
      emailInvites,
      message: message.trim() || null,
      skillDiveAddonId: account.account_available_addons?.find(
        ({ name }) => name === CONTENT_TYPES.SKILL_DIVE
      )?.id,
    });
    if (mounted.current) setSending(false);

    // Refresh the license counts when any license changed.
    const changedSeats =
      result.existingUsers.some(({ changes }) =>
        changes.some(({ kind, result: change }) => kind !== 'team' && change === 'applied')
      ) ||
      (Boolean(result.emailInvites?.sent) &&
        emailInvites.some(({ has_license, has_skill_dive }) => has_license || has_skill_dive));
    if (changedSeats || outcomeHitLicenseError(result)) {
      refreshSession();
      refreshAccounts();
    }

    if (!outcomeFailed(result)) {
      if (existingUsers.length) {
        Notification({
          text: activeUsersInvitedToTeam(existingUsers.length, team.name),
          type: NOTIFICATION_TYPES.SUCCESS,
        });
      }
      if (emailInvites.length) {
        Notification({
          text: pendingUsersInvitedToTeam(emailInvites.length, team.name),
          type: NOTIFICATION_TYPES.SUCCESS,
        });
      }
      onClose(true);
      return;
    }
    if (!existingUsers.length) {
      // Only emails were sent and all were refused: stay on the message to retry.
      const licenseError = result.emailInvites?.licenseError;
      Notification({
        text: licenseError
          ? licenseRejectionMessage(
              licenseError,
              LICENSE_REJECTION_OUTCOMES.NO_EMAIL_INVITATIONS_SENT
            )
          : INVITATIONS_NOT_SENT,
        type: NOTIFICATION_TYPES.ERROR,
      });
      return;
    }
    if (mounted.current) {
      setOutcome(result);
      setStep(WizardStep.OUTCOME);
    }
  };

  const handleBack = () => {
    if (sending) return;
    if (step === WizardStep.ACCESS && editingOnMobile) setEditingOnMobile(false);
    else if (step === WizardStep.MESSAGE) setStep(WizardStep.ACCESS);
    else setStep(WizardStep.SELECT);
  };

  const handleTabChange = (option: string) => {
    // Don't switch tabs while sending.
    if (sending || option === inviteType) return;
    setInviteType(option as InviteType);
    // Close the mobile edit screen, since leaving the tab unmounts it.
    setEditingOnMobile(false);
  };

  const closeModal = () => onClose(outcome ? outcomeSavedAnything(outcome) : undefined);

  const modalWidth =
    (isMobile && '100%') ||
    (inviteType === InviteType.BY_LINK && INVITE_NARROW_STEP_WIDTH) ||
    (step === WizardStep.ERRORS && MODAL_SIZES.LARGE) ||
    (step === WizardStep.ACCESS && SKILL_DIVE_ACCESS_STEP_WIDTH) ||
    INVITE_NARROW_STEP_WIDTH;

  const footer = () => {
    if (step === WizardStep.OUTCOME) {
      return (
        <OutcomeActions>
          <Button $primary onClick={closeModal}>
            {TEAM_INVITE_WIZARD_COPY.DONE}
          </Button>
        </OutcomeActions>
      );
    }
    if (editingOnMobile) return null;
    const nextStep = {
      [WizardStep.SELECT]: {
        label: TEAM_INVITE_WIZARD_COPY.NEXT,
        onPress: handleSelectNext,
        disabled: invalidEmails.length > 0 || (!existingUsers.length && !emails.length),
      },
      [WizardStep.ERRORS]: {
        label: TEAM_INVITE_WIZARD_COPY.REMOVE_USERS,
        onPress: handleRemoveErrors,
        disabled: false,
      },
      [WizardStep.ACCESS]: {
        label: needsMessage ? TEAM_INVITE_WIZARD_COPY.NEXT : TEAM_INVITE_WIZARD_COPY.SAVE_CHANGES,
        onPress: needsMessage ? () => setStep(WizardStep.MESSAGE) : handleSend,
        disabled: (!existingUsers.length && !emailInvites.length) || trainingOverCommitted,
      },
      [WizardStep.MESSAGE]: {
        label: TEAM_INVITE_WIZARD_COPY.SEND_INVITES,
        onPress: handleSend,
        disabled: false,
      },
    }[step];
    return (
      <Footer
        steps={STEP_DOTS}
        stepActive={stepDot(step)}
        topGap={step === WizardStep.ACCESS ? 20 : undefined}
        hasBackButton={step !== WizardStep.SELECT}
        onBackPress={handleBack}
        // Inviting can wait when the team was just created.
        dismissLabel={isInvite ? undefined : TEAM_INVITE_WIZARD_COPY.INVITE_LATER}
        onDismissPress={() => onClose()}
        nextLabel={nextStep.label}
        onNextPress={nextStep.onPress}
        disabled={nextStep.disabled}
        loading={checkingEmails || sending}
      />
    );
  };

  return (
    <Modal
      centered
      visible
      fullscreen={isMobile}
      onCancel={closeModal}
      // Can't close while sending.
      closable={!sending}
      keyboard={!sending}
      footer={null}
      maskClosable={false}
      closeIconColor={colors.fonts.white}
      width={modalWidth}
    >
      <InnerContainer>
        <HeaderContainer>
          <Title>
            {isInvite
              ? inviteUsersToTeamTitle(team.name)
              : TEAM_INVITE_WIZARD_COPY.TEAM_CREATED_TITLE}
          </Title>
          <ToggleButtons {...INVITE_TYPE_OPTIONS} value={inviteType} onChange={handleTabChange} />
        </HeaderContainer>
        <BodyContainer>
          {inviteType === InviteType.BY_LINK ? (
            <AddByLink team={team as Team} handleBackPress={closeModal} />
          ) : (
            <>
              {step === WizardStep.SELECT && (
                // Lock the picks while the emails are checked.
                <StepFieldset disabled={checkingEmails}>
                  <SelectUsersStep
                    users={users}
                    usersStatus={usersStatus}
                    selectedUsers={existingUsers}
                    onToggleUser={handleToggleUser}
                    onRemoveUser={handleRemoveUser}
                    emails={emails}
                    setEmails={setEmails}
                    managedTeamsOnly={managedTeamsOnly}
                  />
                </StepFieldset>
              )}
              {step === WizardStep.ERRORS && <RemoveUsers wrongEmails={errors} />}
              {step === WizardStep.ACCESS && (
                // Lock the rows while saving.
                <AccessStepFieldset disabled={sending}>
                  <AdjustUsersSettings
                    hasTeam
                    teamImplied
                    editsExistingUsers
                    invites={emailInvites}
                    activeUserInvites={existingUsers}
                    onInvitesUpdate={handleAccessUpdate}
                    onInviteRemove={handleRemoveRow}
                    // The fieldset doesn't block a mobile row's tap, so check here.
                    onEdit={(open) => !sending && setEditingOnMobile(open)}
                    isEditing={editingOnMobile}
                    account={account}
                  />
                </AccessStepFieldset>
              )}
              {step === WizardStep.MESSAGE && (
                <AddMessage onMessageUpdate={setMessage} defaultValue={message} />
              )}
              {step === WizardStep.OUTCOME && outcome && (
                <InviteOutcome outcome={outcome} teamName={team.name} />
              )}
              {footer()}
            </>
          )}
        </BodyContainer>
      </InnerContainer>
    </Modal>
  );
};

export default TeamInviteWizard;
